import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const wholesaleBuyersApi = createApi({
  reducerPath: "wholesaleBuyersApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["WholesaleBuyer", "WholesaleBuyerStats"],
  endpoints: (build) => ({
    // Get all buyers
    getAllBuyers: build.query({
      query: ({ buyerType, activityLevel, search, page = 1, limit = 100, starred, vip } = {}) => ({
        url: "/wholesale/buyers",
        method: "GET",
        params: { buyerType, activityLevel, search, page, limit, starred, vip },
      }),
      providesTags: ["WholesaleBuyer"],
    }),

    // Get buyer by ID
    getBuyerById: build.query({
      query: (id) => ({
        url: `/wholesale/buyers/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "WholesaleBuyer", id }],
    }),

    // Create buyer
    createBuyer: build.mutation({
      query: (buyer) => ({
        url: "/wholesale/buyers",
        method: "POST",
        body: buyer,
      }),
      invalidatesTags: ["WholesaleBuyer", "WholesaleBuyerStats"],
    }),

    // Update buyer
    updateBuyer: build.mutation({
      query: ({ id, ...buyer }) => ({
        url: `/wholesale/buyers/${id}`,
        method: "PUT",
        body: buyer,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleBuyer", id },
        "WholesaleBuyer",
        "WholesaleBuyerStats",
      ],
    }),

    // Log a touch, and optionally book the next one in the same request.
    // Two calls would risk logging the contact but losing the follow-up.
    logBuyerContact: build.mutation({
      query: ({ id, followUpDate }) => ({
        url: `/wholesale/buyers/${id}/log-contact`,
        method: "PATCH",
        body: { followUpDate },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleBuyer", id },
        "WholesaleBuyer",
        "WholesaleBuyerStats",
      ],
    }),

    // Delete buyer
    deleteBuyer: build.mutation({
      query: (id) => ({
        url: `/wholesale/buyers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WholesaleBuyer", "WholesaleBuyerStats"],
    }),

    // Toggle star
    toggleBuyerStar: build.mutation({
      query: (id) => ({
        url: `/wholesale/buyers/${id}/star`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "WholesaleBuyer", id },
        "WholesaleBuyer",
      ],
    }),

    // Toggle VIP
    toggleBuyerVip: build.mutation({
      query: (id) => ({
        url: `/wholesale/buyers/${id}/vip`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "WholesaleBuyer", id },
        "WholesaleBuyer",
        "WholesaleBuyerStats",
      ],
    }),

    // Record deal with buyer
    recordDealWithBuyer: build.mutation({
      query: ({ id, dealAmount }) => ({
        url: `/wholesale/buyers/${id}/record-deal`,
        method: "POST",
        body: { dealAmount },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "WholesaleBuyer", id },
        "WholesaleBuyer",
        "WholesaleBuyerStats",
      ],
    }),

    // Get buyer stats
    getBuyerStats: build.query({
      query: () => ({
        url: "/wholesale/buyers/stats",
        method: "GET",
      }),
      providesTags: ["WholesaleBuyerStats"],
    }),

    // Find matching buyers
    findMatchingBuyers: build.query({
      query: ({ price, propertyType, city, beds, baths }) => ({
        url: "/wholesale/buyers/match",
        method: "GET",
        params: { price, propertyType, city, beds, baths },
      }),
    }),
  }),
});

export const {
  useGetAllBuyersQuery,
  useGetBuyerByIdQuery,
  useCreateBuyerMutation,
  useUpdateBuyerMutation,
  useLogBuyerContactMutation,
  useDeleteBuyerMutation,
  useToggleBuyerStarMutation,
  useToggleBuyerVipMutation,
  useRecordDealWithBuyerMutation,
  useGetBuyerStatsQuery,
  useFindMatchingBuyersQuery,
  useLazyFindMatchingBuyersQuery,
} = wholesaleBuyersApi;
